import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLocalizationContext } from '@im/react-localization';

import { usePostToX4A } from './usePostToX4A';
import { useIsAuthenticated } from '../../auth/user-info.hooks';
import { useDecodedToken } from '../../auth/authorization.hook';
import { FRAME_ID, isValidOrigin } from './utils';
import useIM360Origin from './useIM360Origin';

const FRAME_STYLE_ID = 'x4a-frame-style';

type ObjectMap = {
  [key: string]: any;
};

/** Use Xvantage frame: Identify if X4C is currently loaded in an X4A iframe
 * and handles configurations received from the parent application. */
export function useInX4AFrame() {
  const isAuthenticated = useIsAuthenticated();
  const decodedToken = useDecodedToken();
  const context = useLocalizationContext();
  const navigate = useNavigate();
  const { postMessageToX4A } = usePostToX4A();

  const isFrame = window.self !== window.top;
  const ancestors = window.location.ancestorOrigins;
  const IM360_Origin = useIM360Origin();

  const [isInIM360Frame] = useState<boolean>(
    isFrame && ancestors.contains(IM360_Origin)
  );

  const [isInX4AFrame, setIsInX4AFrame] = useState<boolean>(isFrame);

  const listeningForPostMsg = useRef(false);
  const x4cMounted = useRef(false);

  const [hideFooter, setHideFooter] = useState<boolean>(isFrame);
  const [hideHeader, setHideHeader] = useState<boolean>(isFrame);
  const [hideSideNav, setHideSideNav] = useState<boolean>(isFrame);
  const [searchParams, setSearchParams] = useState<ObjectMap>({});

  const [selectedLocale, setSelectedLocale] = useState('');

  const onPostMsgReceived = (event: MessageEvent) => {
    //Messages from invalid origins should not be processed!
    if (!isValidOrigin(event.origin)) return;
    //Xv frame post messages should always contain xv-frame frameId
    if (!event || event.data?.frameId !== FRAME_ID) return;
    const {
      action,
      hideFooter,
      hideHeader,
      hideSideNav,
      locale,
      searchParams,
      styles,
    } = event.data;

    if (!listeningForPostMsg.current && action === 'post-msg-ping') {
      postMessageToX4A('post-msg-ready', { origin: window.location.origin });
      listeningForPostMsg.current = true;
    }

    if (action === 'update-config') {
      setHideFooter(hideFooter ?? true);
      setHideHeader(hideHeader ?? true);
      setHideSideNav(hideSideNav ?? true);

      if (searchParams && Object.keys(searchParams)?.length > 0) {
        setSearchParams(searchParams);
      }

      setIsInX4AFrame(true);

      if (locale) {
        if (!context.supportedLocales.includes(locale)) {
          console.warn(
            `Locale "${locale}" received from X4A frame config is not supported. Language not set. Supported locales: ${context.supportedLocales.toString()}`
          );
        } else {
          setSelectedLocale(locale);
        }
      }

      if (styles) {
        const styleEle = document.getElementById(FRAME_STYLE_ID);
        if (!styleEle) {
          const ele = document.createElement('style');
          ele.id = FRAME_STYLE_ID;
          document.head.append(ele);
        }
        if (styleEle && styles !== styleEle.textContent)
          styleEle.textContent = styles;
      }
    }

    if (action === 'navigate-x4c-to') {
      if (event.data?.route) {
        navigate(event.data?.route);
        return;
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated && isInX4AFrame && !x4cMounted.current) {
      //Inform parent that X4C has mounted
      postMessageToX4A('x4c-mounted', {
        origin: window.location.origin,
        x4cUser: decodedToken?.imoProfile.userName,
      });
      x4cMounted.current = true;

      //Listener receives post messages from X4A
      window.addEventListener('message', onPostMsgReceived);
      return () => window.removeEventListener('message', onPostMsgReceived);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    const currLocale = context.locale;
    if (selectedLocale && currLocale !== selectedLocale) {
      context.setLocale(selectedLocale);
    }
  }, [context, selectedLocale]);

  return useMemo(() => {
    return {
      isInIM360Frame,
      isInX4AFrame,
      xvFrameConfig: {
        hideFooter,
        hideHeader,
        hideSideNav,
        searchParams,
      },
    };
  }, [
    hideFooter,
    hideHeader,
    hideSideNav,
    isInIM360Frame,
    isInX4AFrame,
    searchParams,
  ]);
}
